// Constantes de timing del kiosco — NO modificar sin revisar el HTML original.

export const SCAN_SPEED_MS = {
  fast:   1500,
  normal: 3000,
  slow:   5000,
} as const;

export type ScanSpeed = keyof typeof SCAN_SPEED_MS;

/** Duración en ms de la animación de lectura de tarjeta NFC. */
export const CARD_SCAN_MS = 2200;

/** Segundos antes de que el kiosco regrese al home tras completar un flujo. */
export const KIOSK_AUTO_RESET_SECONDS = 7;

/** Máximo de intentos de acceso admin antes de bloquear el método. */
export const ADMIN_MAX_ATTEMPTS = 2;

/** Template IDs de biométricos (devueltos en biometric_data[].id_template_biometric). */
export const BIOMETRIC_TEMPLATE = {
  FACE_PHOTO: 14,
  PIN:         5,
} as const;

/**
 * @deprecated Use CLOCK_WEB_MODE from attendance/domain/constants/verificationMode.ts instead.
 * These were the old local-only IDs (1=facial, 2=pin, 3=card) before the backend canonical
 * IDs were introduced (11=FACIAL_PC, 13=PIN_PC, 2=CARD). Kept here only to avoid
 * breaking any code that hasn't been migrated yet — remove once all call sites are updated.
 */
// export const VERIFICATION_MODE_ID = {
//   FACIAL: 1,
//   PIN:    2,
//   CARD:   3,
// } as const;

// ── Face recognition (ONNX) ──────────────────────────────────────────────────

/** Dimensión esperada del embedding v2 (MobileFaceNet/ArcFace 512-d).
 * Se compara contra person_embed_v2.length para validar la dim del backend. */
export const EXPECTED_EMBEDDING_DIM = 512 as const;

/** Umbral de cosine similarity para considerar un match como válido [0, 1]. */
export const FACE_MATCH_THRESHOLD_COSINE = 0.6 as const;

// Liveness thresholds `FACE_LIVENESS_HIGH` / `FACE_LIVENESS_LOW` were removed
// by SDD change 2026-07-02-unify-scan-embedding-liveness (Slice B). The
// multi-frame `LivenessAggregator` now reads `passiveHigh` / `passiveLow` from
// `useQualitySettings` (persisted in db.settings), unifying scan and enroll
// under a single configuration source.

/** Tiempo máximo de escaneo facial antes de transitar a error (ms).
 * 12s permite que celulares gama baja completen embedding + liveness + matching. */
export const FACE_MATCH_TIMEOUT_MS = 12_000 as const;

/** Frames por segundo a los que se corre la inferencia (throttle). */
export const FACE_FRAME_THROTTLE_FPS = 5 as const;

/** Calidad JPEG para la foto del marcaje (0-1). */
export const FACE_PHOTO_JPEG_QUALITY = 0.85 as const;

/** Debounce ms para reconstruir el card number desde el lector HID (emula teclado).
 * El lector envía cada dígito como keypress; esperamos `CARD_READER_DEBOUNCE_MS` de silencio
 * antes de considerar la lectura completa. */
export const CARD_READER_DEBOUNCE_MS = 1000 as const;

/** Umbral de cosine similarity para detectar duplicados durante el enrol facial (0, 1].
 * Más estricto que FACE_MATCH_THRESHOLD_COSINE (0.60) porque detecta 1:N sin claim de identidad. */
export const FACE_ENROLL_DUPLICATE_THRESHOLD_COSINE = 0.75 as const;

/** Debounce ms para evitar el parpadeo visual `comparing → scanning` en ScanningPage.
 *  3 ciclos de scan a 200 ms cada uno. Solo afecta la fase visual; la fase real
 *  de useScanLifecycle sigue cambiando en tiempo real para navegación. */
export const VISUAL_PHASE_DEBOUNCE_MS = 600 as const;

/** Inactivity timeout for the admin session (ms).
 * After this period of no user interaction inside the /admin subtree,
 * RequireAdmin automatically locks the session and redirects to home.
 * Slice F — useIdleTimeout hook consumes this constant. */
export const ADMIN_IDLE_TIMEOUT_MS = 180_000 as const;

/** Polling interval (ms) for useFaceEmbeddingsCountQuery when count === 0.
 * The query polls this often until embeddings are seeded OR the empty-attempt cap
 * (FACE_EMBEDDINGS_MAX_EMPTY_ATTEMPTS) is reached. */
export const FACE_EMBEDDINGS_EMPTY_POLL_MS = 2000 as const;

/** Maximum number of consecutive empty (`count === 0`) polls before
 * useFaceEmbeddingsCountQuery stops polling and exposes noFacialEnrollments=true.
 * Keeps the ScanningPage from spinning forever when the active AccessRole has
 * zero seeded facial embeddings — operator can still use PIN or Card. */
export const FACE_EMBEDDINGS_MAX_EMPTY_ATTEMPTS = 3 as const;
